import React, { ChangeEvent, useMemo } from 'react';

import { Typography, LinearProgress } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';

import { Province } from '../../../@types/KingdomResponse';
import { ArmyLite, KdTabColumnVisibility } from '../../../@types/State';
import { autosizeOptions } from '../../../Constants';
import { useIntelState } from '../../../context/IntelState';
import { armyGridColDefinitions } from '../columnDefinitions/armiesColumnDefinitions';
import GridViewToolBarNew from '../components/GridViewToolBarNew';
import { StripedDataGrid } from '../components/StripedDataGrid';
import { getArmiesData } from '../helpers/intelGridHelpers';

const cellCss = {
  paddingLeft: '5px',
  paddingRight: '5px',
};

interface Props {
  dataSet: Province[] | undefined;
  isViewingEnemy: boolean;
}

const GridView: React.FC<Props> = ({ dataSet, isViewingEnemy }) => {
  const { intelState, intelDispatch } = useIntelState();
  const apiRef = useGridApiRef();
  const armyRows: ArmyLite[] | null = useMemo(() => {
    const races =
      intelState.server === 1 ? intelState.racesWol : intelState.racesGen;
    return getArmiesData(dataSet, races);
  }, [dataSet, intelState]);

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    const colSet = isViewingEnemy ? 'enemy' : 'own';
    const fileReader = new FileReader();
    fileReader.readAsText(e.target.files[0], 'UTF-8');
    fileReader.onload = (e) => {
      if (e.target?.result && typeof e.target.result === 'string') {
        const json: KdTabColumnVisibility = JSON.parse(e.target.result);
        intelDispatch({
          type: 'SET_TAB_COL_VISIBILITY',
          payload: {
            json,
            currentTab: 'armies',
            colSet,
          },
        });
        window.alert('Navigate to a different tab to see the changes.');
      }
    };
  };

  return armyRows ? (
    <div
      style={{
        height: '100%',
        maxWidth: '100%',
      }}
    >
      <StripedDataGrid
        autoHeight
        rows={armyRows}
        apiRef={apiRef}
        autosizeOptions={autosizeOptions}
        autosizeOnMount={false}
        columns={armyGridColDefinitions}
        density='compact'
        slots={{
          toolbar: GridViewToolBarNew,
          columnMenuUnsortIcon: null,
        }}
        slotProps={{
          toolbar: {
            isViewingEnemy,
            handleFileUpload,
            isViewingRecent: false,
          },
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        loading={intelState.loading}
        hideFooterPagination
        hideFooter
        disableColumnMenu
        sx={{
          '& .MuiDataGrid-columnHeader': cellCss,
          '& .MuiDataGrid-cell': cellCss,
        }}
      />
    </div>
  ) : (
    <Typography variant='body1' sx={{ marginTop: 3 }}>
      No Kingdom Data found
    </Typography>
  );
};

export default GridView;
